/**
 * Bundle 76 - Admin Survey Console.
 *
 * Full super-admin survey management at /admin/events/{id}/survey.
 * Sections:
 *   - Campaign controls (intro edits, close dates, games toggle,
 *     recap photos, highlights, statistics)
 *   - Recipients preview (counts + eligible + excluded reasons)
 *   - Send test / Send live / Send reminder
 *   - Responses list + CSV export
 *   - Testimonials moderation
 *   - Attendance corrections queue with Verify button
 *   - Direct + missed connection captures (data only; workflow in Bundle 78)
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Send, Users, Star, Download, Mail, ShieldCheck, RefreshCw,
  ArrowLeft, CheckCircle2, XCircle, Sparkles, Pause, Play,
} from 'lucide-react';
import { toast } from 'sonner';
import { SentTabPanel } from './components/SentTabPanel';
import { ResponseDetailModal } from './components/ResponseDetailModal';
import { ParticipantsTab } from './components/ParticipantsTab';
import { SurveyOverviewTab } from './components/SurveyOverviewTab';
import { ConnectionsTabPanel } from './components/ConnectionsTabPanel';
import { ExcludeRatingsDialog } from './components/ExcludeRatingsDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const AdminSurveyConsolePage = () => {
  const { id: eventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Bundle 130 amendment #3 - super_admin toggle to include QA
  // rows on the Responses tab. State persists via `include_test`
  // URL query param so refresh + deep-link both survive.
  const includeTest = searchParams.get('include_test') === 'true';
  const setIncludeTest = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) { next.set('include_test', 'true'); }
    else { next.delete('include_test'); }
    setSearchParams(next, { replace: true });
  };
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [responses, setResponses] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [directConnections, setDirectConnections] = useState([]);
  const [missedConnections, setMissedConnections] = useState([]);
  const [busy, setBusy] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [previewVariant, setPreviewVariant] = useState(null);
  // Bundle 103 - sendLive result modal with skipped_breakdown.
  const [sendResult, setSendResult] = useState(null);
  // Bundle 108 - inline Resume banner state + Responses detail modal.
  const [resumeReason, setResumeReason] = useState('');
  const [detailResponseId, setDetailResponseId] = useState(null);
  // Bundle 128 - Force-complete stuck survey response (super_admin only).
  const [forceCompleteRow, setForceCompleteRow] = useState(null);
  const [forceReason, setForceReason] = useState('');
  const [forceBusy, setForceBusy] = useState(false);
  // Bundle 130 amendment - per-row Rescan/Replay via rescan-v2.
  const [rescanRow, setRescanRow] = useState(null);
  const [rescanBusy, setRescanBusy] = useState(false);
  const [rescanDryRun, setRescanDryRun] = useState(null);
  // Bundle 181 - super-admin rating exclusion. Row + mode
  // (exclude|include) that the dialog acts on, plus a filter
  // chip that shows only excluded responses.
  const [excludeRow, setExcludeRow] = useState(null);
  const [excludeMode, setExcludeMode] = useState('exclude');
  const [showExcludedOnly, setShowExcludedOnly] = useState(false);
  const isSuperAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u.role === 'super_admin';
    } catch { return false; }
  })();
  // Bundle 159 - Testimonials tab enhancements.
  const [testimonialFilter, setTestimonialFilter] = useState('all');
  const [testimonialDraftBusyId, setTestimonialDraftBusyId] = useState(null);
  const [testimonialEditRowId, setTestimonialEditRowId] = useState(null);
  const [testimonialEditText, setTestimonialEditText] = useState('');
  const [testimonialEditNote, setTestimonialEditNote] = useState('');
  const [testimonialEditBusy, setTestimonialEditBusy] = useState(false);
  const [testimonialAiDraftById, setTestimonialAiDraftById] = useState({});

  const consentedEmptyRows = useMemo(() => (
    (testimonials || []).filter((t) => (
      t.permission_granted
        && !((t.current_display_text || t.original_text || '').trim())
    ))
  ), [testimonials]);

  const visibleTestimonials = useMemo(() => {
    if (testimonialFilter === 'consented_empty') {
      return consentedEmptyRows;
    }
    return testimonials;
  }, [testimonialFilter, testimonials, consentedEmptyRows]);

  const startEditTestimonial = (t) => {
    setTestimonialEditRowId(t.id);
    setTestimonialEditText(t.current_display_text || t.original_text || '');
    setTestimonialEditNote('');
  };
  const cancelEditTestimonial = () => {
    setTestimonialEditRowId(null);
    setTestimonialEditText('');
    setTestimonialEditNote('');
  };
  const saveEditTestimonial = async (tid) => {
    if (!(testimonialEditText || '').trim()) {
      toast.error('Text is required.');
      return;
    }
    setTestimonialEditBusy(true);
    try {
      const { data } = await api.post(
        `/admin/testimonials/${tid}/edit-text`,
        { text: testimonialEditText, note: testimonialEditNote || null },
      );
      if (data?.status === 'updated') {
        toast.success('Edit saved.');
        setTestimonials((rows) => rows.map((r) => (
          r.id === tid
            ? {
              ...r,
              current_display_text: data.text,
              text_edited_since_publish: data.text_edited_since_publish,
            }
            : r
        )));
      } else if (data?.status === 'noop') {
        toast.info('No change (text identical).');
      }
      cancelEditTestimonial();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail?.code
        || 'Edit failed.');
    } finally { setTestimonialEditBusy(false); }
  };

  const draftWithAiForRow = async (t) => {
    setTestimonialDraftBusyId(t.id);
    try {
      const { data } = await api.post(
        `/admin/testimonials/${t.id}/draft-with-ai`, {},
      );
      if (data?.status === 'pending_admin_review'
          && data?.ai_draft?.text) {
        setTestimonialAiDraftById((s) => ({
          ...s,
          [t.id]: data.ai_draft.text,
        }));
        setTestimonialEditRowId(t.id);
        setTestimonialEditText(data.ai_draft.text);
        setTestimonialEditNote('AI-drafted; review and approve.');
        toast.success('AI draft ready. Review and approve.');
      } else if (data?.status === 'insufficient_source') {
        toast.warning(
          'Not enough survey material to draft a testimonial.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail?.code
        || 'Draft failed.');
    } finally { setTestimonialDraftBusyId(null); }
  };

  const approveAiDraftForRow = async (tid) => {
    if (!(testimonialEditText || '').trim()) {
      toast.error('Text is required.');
      return;
    }
    setTestimonialEditBusy(true);
    try {
      await api.post(
        `/admin/testimonials/${tid}/approve`,
        { text_final: testimonialEditText,
          note: testimonialEditNote || null },
      );
      toast.success('Testimonial approved and published.');
      setTestimonials((rows) => rows.map((r) => (
        r.id === tid
          ? {
            ...r,
            current_display_text: testimonialEditText,
            status: 'published',
          }
          : r
      )));
      setTestimonialAiDraftById((s) => {
        const n = { ...s }; delete n[tid]; return n;
      });
      cancelEditTestimonial();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail?.code
        || 'Approve failed.');
    } finally { setTestimonialEditBusy(false); }
  };

  const runForceComplete = async () => {
    if (!forceCompleteRow) return;
    if (!forceReason || forceReason.trim().length < 3) {
      toast.error('Please provide a reason (min 3 chars).');
      return;
    }
    setForceBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/force-complete-survey-response',
        { response_id: forceCompleteRow.id,
          reason: forceReason.trim() },
      );
      toast.success(data.already_submitted
        ? 'Response already submitted. Audit log recorded.'
        : 'Response marked as completed and hooks replayed.');
      setForceCompleteRow(null); setForceReason('');
      // Trigger a fresh responses fetch (setCampaign is a marker
      // change enough to re-run the effect below).
      setCampaign((c) => c ? { ...c, _refetch: Date.now() } : c);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Force-complete failed.');
    } finally { setForceBusy(false); }
  };

  // Bundle 130 amendment - open the rescan-v2 modal for a row.
  const openRescanForRow = async (row) => {
    setRescanRow(row); setRescanDryRun(null);
    // Fire dry-run immediately to show planned targets.
    try {
      const { data } = await api.post(
        '/admin/incident/rescan-connection-intent-v2',
        { response_id: row.id, dry_run: true },
      );
      setRescanDryRun(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not dry-run rescan.');
    }
  };

  const applyRescanForRow = async () => {
    if (!rescanRow) return;
    setRescanBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/rescan-connection-intent-v2',
        { response_id: rescanRow.id, dry_run: false },
      );
      const created = data?.introductions?.inserted || 0;
      const already = data?.introductions?.already_exists || 0;
      toast.success(
        `Applied. Created ${created}, already existed ${already}.`,
      );
      setRescanRow(null); setRescanDryRun(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Rescan apply failed.');
    } finally { setRescanBusy(false); }
  };

  const resumeCampaign = async () => {
    setBusy(true);
    try {
      await api.post(
        `/admin/events/${eventId}/survey/campaign/status`,
        { status: 'active',
            reason: resumeReason || 'inline-resume-from-console' },
      );
      toast.success('Campaign resumed.');
      setResumeReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
                        || 'Could not resume campaign.');
    } finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const respPath = includeTest
        ? `/admin/events/${eventId}/survey/responses?include_test=true`
        : `/admin/events/${eventId}/survey/responses`;
      const [c, r, resp, t, corr, dc, mc] = await Promise.all([
        api.get(`/admin/events/${eventId}/survey/campaign`),
        api.get(`/admin/events/${eventId}/survey/recipients-preview`),
        api.get(respPath),
        api.get(`/admin/events/${eventId}/survey/testimonials`),
        api.get(`/admin/events/${eventId}/survey/attendance-corrections`),
        api.get(`/admin/events/${eventId}/survey/direct-connections`),
        api.get(`/admin/events/${eventId}/survey/missed-connections`),
      ]);
      setCampaign(c.data.campaign);
      setRecipients(r.data);
      setResponses(resp.data);
      setTestimonials(t.data.testimonials || []);
      setCorrections(corr.data.corrections || []);
      setDirectConnections(dc.data.reports || []);
      setMissedConnections(mc.data.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load survey');
    } finally { setBusy(false); }
  }, [eventId, includeTest]);
  useEffect(() => { load(); }, [load]);

  const patchCampaign = async (patch) => {
    try {
      const { data } = await api.put(`/admin/events/${eventId}/survey/campaign`, patch);
      setCampaign(data.campaign);
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post(`/admin/events/${eventId}/survey/send-test`);
      setTestResults(data.results || []);
      toast.success(`Test dispatched (${data.results.length} variants). Click the links below to walk through the survey.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send test failed'); }
  };

  const openPreview = (variant) => {
    // Bundle 78 Task 5 - in-app preview (side panel) using the same
    // real HMAC token, so admins can review the survey without an
    // email round-trip. Submissions from the preview iframe land in
    // the test_survey_responses bucket, same as send-test.
    setPreviewVariant(variant);
  };

  const sendLive = async () => {
    if (!window.confirm(`Send survey to ${recipients?.counts?.eligible || 0} recipients now?`)) return;
    try {
      const { data } = await api.post(`/admin/events/${eventId}/survey/send-live`, {
        idempotency_key: `admin-live-${Date.now()}`,
      });
      // Bundle 103 - open a result modal instead of a toast so the
      // admin can see the per-reason breakdown. Toast still fires
      // as a confirmation.
      setSendResult({
        sent: data.sent ?? data.job?.dispatched ?? 0,
        skipped: data.skipped ?? data.job?.skipped_total ?? 0,
        skipped_breakdown: data.skipped_breakdown
          || data.job?.skipped_breakdown || {},
        override_reasons_used: data.job?.override_reasons_used || [],
      });
      toast.success(`Dispatched: ${data.sent ?? data.job?.dispatched ?? 0} sent, ${data.skipped ?? data.job?.skipped_total ?? 0} skipped`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send live failed'); }
  };

  // Bundle 134 patch: modal-driven reminder flow with dry-run
  // recipient counts + rendered email preview.
  const [reminderModal, setReminderModal] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);

  const openReminderModal = async () => {
    setReminderBusy(true);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/survey/send-reminder?dry_run=true`,
      );
      setReminderModal(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reminder preview failed');
    } finally { setReminderBusy(false); }
  };

  const applyReminder = async () => {
    setReminderBusy(true);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/survey/send-reminder`,
      );
      toast.success(
        `Reminder: ${data.sent} sent, `
          + `${data.skipped_opted_out} opted out, `
          + `${data.skipped_invalid_email} invalid emails, `
          + `${data.skipped_unresolvable_wildcards} unresolvable.`,
      );
      setReminderModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reminder failed');
    } finally { setReminderBusy(false); }
  };

  const sendReminder = () => { openReminderModal(); };

  const closeCampaign = async () => {
    if (!window.confirm('Close the campaign? Past responses are preserved.')) return;
    try {
      await api.post(`/admin/events/${eventId}/survey/close`);
      toast.success('Campaign closed');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Close failed'); }
  };

  const downloadCsv = async () => {
    try {
      const r = await api.get(`/admin/events/${eventId}/survey/responses.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Dostination-survey-${eventId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('CSV export failed'); }
  };

  const verifyCorrection = async (correctionId, verified) => {
    try {
      await api.post(`/admin/events/${eventId}/survey/attendance-corrections/${correctionId}/verify`, {
        verified, admin_note: '',
      });
      toast.success(verified ? 'Marked verified. Rating will count.' : 'Rejected.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verify failed'); }
  };

  const moderateTestimonial = async (id, status) => {
    try {
      await api.post(`/admin/events/${eventId}/survey/testimonials/${id}/moderate`, { status });
      toast.success(`Testimonial marked ${status}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Moderate failed'); }
  };

  if (!campaign) {
    return <div className="p-6 text-neutral-500">Loading survey console...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-survey-console-page">
      {/* Bundle 118 - breadcrumb for the Survey Ops group. */}
      <nav className="text-[11px] text-neutral-500 mb-2 flex items-center gap-1.5"
                data-testid="survey-console-breadcrumb"
                aria-label="Breadcrumb">
        <Link to="/admin/events" className="hover:text-primary-900">
          Admin
        </Link>
        <span>/</span>
        <Link to="/admin/surveys" className="hover:text-primary-900">
          Survey Ops
        </Link>
        <span>/</span>
        <Link to={`/admin/events/${eventId}/edit`}
                    className="hover:text-primary-900">
          Event
        </Link>
        <span>/</span>
        <span className="text-neutral-700">Survey console</span>
      </nav>
      <Link to={`/admin/events/${eventId}/edit`} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-primary-900 mb-3">
        <ArrowLeft className="h-3 w-3" /> Back to event
      </Link>
      {campaign.status === 'paused' ? (
        <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-4"
              data-testid="survey-paused-banner">
          <div className="flex items-start gap-3">
            <Pause className="h-5 w-5 text-yellow-800 mt-0.5" />
            <div className="flex-1">
              <div className="text-yellow-900 font-semibold">
                Campaign is paused
              </div>
              <div className="text-xs text-yellow-800 mt-1">
                Send Live and Reminder are disabled while paused. Resume the campaign to dispatch to outstanding recipients.
              </div>
              <div className="mt-3 flex flex-wrap gap-2 items-start">
                <textarea
                  value={resumeReason}
                  onChange={(e) => setResumeReason(e.target.value)}
                  placeholder="Optional reason for audit log"
                  className="text-xs px-2 py-1 border border-yellow-300 rounded-md bg-white w-72 h-16"
                  data-testid="survey-resume-reason"
                />
                <Button size="sm"
                          className="bg-success-600 text-white hover:bg-success-700"
                          onClick={resumeCampaign}
                          disabled={busy}
                          data-testid="survey-resume-btn">
                  <Play className="h-4 w-4 mr-2" />
                  Resume campaign
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800 mb-1"
                data-testid="survey-console-eyebrow">Post-event survey</div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Survey console</h1>
          <div className="text-xs text-neutral-600 mt-1">
            Status: <Badge className="ml-1" data-testid="survey-console-status-badge">{campaign.status}</Badge>
            <span className="ml-3" data-testid="survey-console-qs-version">
              Question set: {campaign.question_set?.version || 'unknown'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="survey-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={sendTest} data-testid="survey-send-test-btn"
                        title="Sends a preview-only test email to your own admin address. Bundle 117 preview token, never contaminates real recipient state.">
            <Mail className="h-3.5 w-3.5 mr-1" /> Send test
          </Button>
          <Button size="sm" variant="outline" onClick={() => openPreview('attendee')} data-testid="survey-preview-attendee-btn">
            Preview: attendee
          </Button>
          <Button size="sm" variant="outline" onClick={() => openPreview('non_attendee')} data-testid="survey-preview-non-attendee-btn">
            Preview: non-attendee
          </Button>
          {/* Bundle 118 - legacy "Send live (N)" toolbar button removed.
              All send actions live on the Overview tab and the
              Participants tab (Bundle 114). The count on this button
              read the Recipients-tab stale eligible number and could
              double-send once Bundle 114 owned the participants
              surface. */}
          <Button size="sm" variant="outline" onClick={sendReminder}
                    disabled={campaign.status === 'paused'}
                    data-testid="survey-send-reminder-btn">Reminder</Button>
          {campaign.status !== 'closed' ? (
            <Button size="sm" variant="outline" onClick={closeCampaign} data-testid="survey-close-btn">Close campaign</Button>
          ) : null}
        </div>
      </div>

      {/* Bundle 78 Task 4 - test-send preview URLs (clickable) */}
      {testResults && testResults.length > 0 ? (
        <div className="mb-6 rounded-md border border-secondary-200 bg-secondary-50/50 p-3" data-testid="survey-test-results-panel">
          <div className="text-[11px] uppercase tracking-widest text-secondary-800 mb-2">
            Test survey links (valid 24h, submissions land in a separate test bucket)
          </div>
          <div className="grid gap-1.5 text-sm">
            {testResults.map((r) => (
              <div key={r.variant} className="flex items-center gap-2">
                <Badge variant="outline" className="capitalize">{r.variant.replace('_', '-')}</Badge>
                <a
                  href={`/survey/${r.token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary-800 underline hover:text-secondary-900 font-mono text-xs break-all"
                  data-testid={`survey-test-link-${r.variant}`}
                >
                  {`/survey/${r.token}`}
                </a>
                {r.sent === false ? (
                  <span className="text-xs text-neutral-500">(email {r.reason || 'not sent'})</span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Bundle 78 Task 5 - in-app preview panel */}
      {previewVariant ? (
        <div className="mb-6" data-testid="survey-preview-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest text-secondary-800">
              In-app preview: {previewVariant === 'attendee' ? 'attendee survey' : 'non-attendee survey'}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setPreviewVariant(null)} data-testid="survey-preview-close-btn">
              Close preview
            </Button>
          </div>
          <SurveyPreviewFrame eventId={eventId} variant={previewVariant} />
        </div>
      ) : null}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="survey-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="setup" data-testid="survey-tab-setup">Setup</TabsTrigger>
          <TabsTrigger value="participants" data-testid="survey-tab-participants">Participants</TabsTrigger>
          <TabsTrigger value="sent" data-testid="survey-tab-sent">Sent</TabsTrigger>
          <TabsTrigger value="responses" data-testid="survey-tab-responses">Responses ({responses?.counts?.total || 0})</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="survey-tab-testimonials">Testimonials ({testimonials.length})</TabsTrigger>
          <TabsTrigger value="corrections" data-testid="survey-tab-corrections">Corrections ({corrections.length})</TabsTrigger>
          {/* Bundle 158 - filter legacy `missed_connection_requests`
              rows out of the header count so it matches the
              tabbed panel below (which defaults include_legacy=
              false). The legacy chip inside the panel still
              surfaces the legacy universe. */}
          <TabsTrigger value="connections" data-testid="survey-tab-connections">Connections ({directConnections.length + missedConnections.filter((r) => r.source !== 'legacy_missed_connection_requests').length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-4" data-testid="survey-tab-overview-panel">
          <SurveyOverviewTab
            eventId={eventId}
            campaignStatus={campaign?.status}
            busy={busy}
            onSendTest={sendTest}
            onSendLive={sendLive}
            onSendReminder={sendReminder}
            onOpenResponse={(rid) => { setDetailResponseId(rid); }}
          />
        </TabsContent>

        <TabsContent value="setup" className="pt-4">
          <Card><CardContent className="p-4 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Attendee intro</div>
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const { data } = await api.post(`/admin/events/${eventId}/survey/ai/draft-intro`);
                    if (data?.attendee_intro) setCampaign((c) => ({ ...c, intro_attendee: data.attendee_intro }));
                    if (data?.non_attendee_intro) setCampaign((c) => ({ ...c, intro_non_attendee: data.non_attendee_intro }));
                    toast.success('Draft ready. Edit and save when you like it.');
                  } catch (e) { toast.error(e?.response?.data?.detail?.reason || 'Draft failed'); }
                }} data-testid="setup-draft-with-dost-btn">
                  Draft with Dost
                </Button>
              </div>
              <Textarea value={campaign.intro_attendee || ''} onChange={(e) => setCampaign({ ...campaign, intro_attendee: e.target.value })} className="min-h-[80px]" data-testid="setup-intro-attendee" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Non-attendee intro</div>
              <Textarea value={campaign.intro_non_attendee || ''} onChange={(e) => setCampaign({ ...campaign, intro_non_attendee: e.target.value })} className="min-h-[80px]" data-testid="setup-intro-non-attendee" />
            </div>

            {/* Bundle 79 Task A - AI suggestion hooks for highlights/stats/recap */}
            <div className="grid gap-2 sm:grid-cols-3" data-testid="setup-ai-suggest-row">
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/events/${eventId}/survey/ai/suggest-highlights`);
                  setCampaign((c) => ({ ...c, highlights: (data.highlights || []).map((h) => ({ label: h, text: h })) }));
                  toast.success('Highlights suggested. Edit and save when ready.');
                } catch (e) { toast.error(e?.response?.data?.detail?.reason || 'Suggest failed'); }
              }} data-testid="setup-suggest-highlights-btn">Suggest highlights</Button>
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/events/${eventId}/survey/ai/suggest-statistics`);
                  setCampaign((c) => ({ ...c, statistics: data.statistics || [] }));
                  toast.success('Statistics derived from real event data.');
                } catch (e) { toast.error(e?.response?.data?.detail?.reason || 'Suggest failed'); }
              }} data-testid="setup-suggest-statistics-btn">Suggest statistics</Button>
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/events/${eventId}/survey/ai/draft-recap`);
                  setCampaign((c) => ({ ...c, recap_message: data.recap }));
                  toast.success('Recap drafted for the non-attendee card.');
                } catch (e) { toast.error(e?.response?.data?.detail?.reason || 'Draft failed'); }
              }} data-testid="setup-draft-recap-btn">Draft recap</Button>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox checked={!!campaign.games_toggle} onCheckedChange={(v) => setCampaign({ ...campaign, games_toggle: !!v })} />
              <span className="text-sm">Include games question</span>
              <Input placeholder="Games question label" value={campaign.games_label || ''} onChange={(e) => setCampaign({ ...campaign, games_label: e.target.value })} className="w-64 ml-3" />
            </div>
            {/* Bundle 77 - auto-schedule opt-out. Default is opted IN. */}
            <div className="flex items-center gap-3" data-testid="setup-auto-schedule-row">
              <Checkbox
                checked={campaign.auto_send_24h_after_end !== false}
                onCheckedChange={(v) => setCampaign({ ...campaign, auto_send_24h_after_end: !!v })}
                data-testid="setup-auto-schedule-checkbox"
              />
              <span className="text-sm">Automatically send this survey 24 hours after event end</span>
              <span className="text-[11px] text-neutral-500">Unchecked = manual send only</span>
            </div>
            <div className="flex items-center gap-2">
              <Input placeholder="Open at (ISO)" value={campaign.open_at || ''} onChange={(e) => setCampaign({ ...campaign, open_at: e.target.value })} className="w-64" />
              <Input placeholder="Close at (ISO)" value={campaign.close_at || ''} onChange={(e) => setCampaign({ ...campaign, close_at: e.target.value })} className="w-64" />
            </div>
            <Button size="sm" onClick={() => patchCampaign({
              intro_attendee: campaign.intro_attendee,
              intro_non_attendee: campaign.intro_non_attendee,
              games_toggle: campaign.games_toggle,
              games_label: campaign.games_label,
              open_at: campaign.open_at || null,
              close_at: campaign.close_at || null,
              auto_send_24h_after_end: campaign.auto_send_24h_after_end !== false,
            })} data-testid="setup-save-btn">Save setup</Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="participants" className="pt-4">
          <ParticipantsTab eventId={eventId} />
        </TabsContent>

        {/* Bundle 118 - Recipients tab REMOVED. Its "eligible/excluded"
            counters read stale pre-Bundle-114 data and misled admins
            (the "Send live (2)" bug that could double-send). Send
            actions now live on Overview + Participants. The
            eligible/excluded breakdown is preserved in the Bundle 108
            Participants console tab which reads active-campaign data. */}

        <TabsContent value="sent" className="pt-4" data-testid="survey-tab-sent-panel">
          <SentTabPanel eventId={eventId} />
        </TabsContent>

        <TabsContent value="responses" className="pt-4">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap mb-3 text-xs">
              <Badge>Total: {responses?.counts?.total || 0}</Badge>
              <Badge className="bg-emerald-50 text-emerald-800">Submitted: {responses?.counts?.submitted || 0}</Badge>
              <Badge className="bg-amber-50 text-amber-800">Started: {responses?.counts?.started || 0}</Badge>
              <Badge className="bg-primary-50 text-primary-800">Attendee: {responses?.counts?.attendee || 0}</Badge>
              <Badge>Rating counts: {responses?.counts?.rating_counts || 0}</Badge>
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/events/${eventId}/survey/ai/summarize-responses?refresh=1`);
                  toast.success('Summary refreshed.');
                  setCampaign((c) => ({ ...c, ai_summary_cache: data }));
                } catch (e) { toast.error(e?.response?.data?.detail || 'Summary failed (need >=5 submitted responses)'); }
              }} data-testid="responses-summarize-btn">
                Summarize responses
              </Button>
              <Button size="sm" variant="outline" onClick={downloadCsv} className="ml-auto" data-testid="responses-csv-btn">
                <Download className="h-3 w-3 mr-1" /> CSV
              </Button>
            </div>
            {/* Bundle 181 - "Show excluded" filter chip. Toggle
                filters the list to only responses a super-admin
                has flagged out of rating aggregates. Excluded
                rows also render a badge inline. */}
            {isSuperAdmin && (
              <div
                className="mb-3 flex items-center gap-2 flex-wrap"
                data-testid="responses-exclusion-filter-row"
              >
                <button
                  type="button"
                  onClick={() => setShowExcludedOnly(false)}
                  className={
                    'rounded-full border px-3 py-1 text-xs '
                    + (!showExcludedOnly
                      ? 'bg-primary-900 text-white border-primary-900'
                      : 'bg-white text-neutral-700 border-neutral-300 hover:border-primary-400')
                  }
                  data-testid="responses-filter-all"
                >
                  All responses
                </button>
                <button
                  type="button"
                  onClick={() => setShowExcludedOnly(true)}
                  className={
                    'rounded-full border px-3 py-1 text-xs '
                    + (showExcludedOnly
                      ? 'bg-rose-800 text-white border-rose-800'
                      : 'bg-white text-neutral-700 border-neutral-300 hover:border-rose-400')
                  }
                  data-testid="responses-filter-excluded"
                >
                  Show excluded ({(responses?.responses || []).filter((r) => r.rating_excluded_from_aggregates).length})
                </button>
              </div>
            )}
            {isSuperAdmin && (
              <div
                className="mb-3 rounded-md border border-secondary-200 bg-secondary-50/40 p-2 flex items-center justify-between gap-3 text-xs"
                data-testid="responses-include-test-row"
              >
                <div>
                  <span className="uppercase tracking-widest text-[10px] text-secondary-800 font-semibold">
                    Super admin
                  </span>
                  <span className="ml-2 text-primary-900">
                    Include QA/test rows
                  </span>
                  <div className="text-[10px] text-neutral-500 mt-0.5">
                    Off by default. Adds `?include_test=true` to the fetch.
                    Persisted in the URL so refresh + deep-links survive.
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary-800"
                    checked={includeTest}
                    onChange={(e) => setIncludeTest(e.target.checked)}
                    data-testid="responses-include-test-toggle"
                  />
                  <span className="text-primary-900">
                    {includeTest ? 'ON' : 'OFF'}
                  </span>
                </label>
              </div>
            )}
            {campaign.ai_summary_cache ? (
              <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50/50 p-3 text-xs text-primary-900" data-testid="responses-ai-summary">
                <div className="text-[10px] uppercase tracking-widest text-secondary-800 mb-1">Dost AI summary * n={campaign.ai_summary_cache.n_responses}</div>
                <p><b>Themes:</b> {campaign.ai_summary_cache.themes}</p>
                <p><b>Sentiment:</b> {campaign.ai_summary_cache.sentiment}</p>
                <p><b>Suggested improvement:</b> {campaign.ai_summary_cache.suggested_improvement}</p>
              </div>
            ) : null}
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="responses-table">
                <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-1.5 px-2">Recipient</th>
                    <th className="text-left py-1.5 px-2">Variant</th>
                    <th className="text-left py-1.5 px-2">Rating</th>
                    <th className="text-left py-1.5 px-2">Counts</th>
                    <th className="text-left py-1.5 px-2">Submitted</th>
                    <th className="text-left py-1.5 px-2">Discovery</th>
                    <th className="text-left py-1.5 px-2"
                        data-testid="responses-intros-header">
                      Introductions
                    </th>
                    {isSuperAdmin && (
                      <th className="text-left py-1.5 px-2"
                          data-testid="responses-action-header">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(responses?.responses || [])
                    .filter((r) => (
                      !showExcludedOnly
                      || r.rating_excluded_from_aggregates
                    ))
                    .map((r) => {
                    const isDrifted = (() => {
                      // Bundle 145 P0-4 fix: prior logic flagged
                      // ANY response whose discovery_source value
                      // was in the legacy set (including 'instagram'
                      // which is also a canonical value in the new
                      // schema). That produced false "[drift]" pills
                      // on fresh submissions where the user simply
                      // picked "Instagram" as their real source.
                      //
                      // True drift = the row has the LEGACY field
                      // (`answers.discovery_source`) populated but
                      // NOT the new field
                      // (`answers.self_reported_discovery_source`).
                      // A row carrying the new field is by
                      // definition on-schema regardless of value.
                      const newField = r.answers
                        ?.self_reported_discovery_source;
                      const legacyField = r.answers?.discovery_source;
                      const fut = r.answers?.future_event_likelihood;
                      const LEGACY_FUT = new Set([
                        'very_likely', 'likely', 'unlikely',
                        'very_unlikely', 'no',
                      ]);
                      // Discovery-source drift only when the new
                      // field is missing AND the legacy field is
                      // populated with any value.
                      if (!newField && typeof legacyField === 'string'
                          && legacyField.length > 0) return true;
                      // Future-likelihood drift kept as-is
                      // (pre-Bundle-89 schema used numeric values).
                      if (typeof fut === 'number') return true;
                      if (typeof fut === 'string'
                          && LEGACY_FUT.has(fut)) return true;
                      return false;
                    })();
                    const variantResolved = r.variant_resolved
                      || (r.is_attendee_at_submit ? 'attendee' : 'non-attendee');
                    return (
                      <tr key={r.id}
                            className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                            onClick={() => setDetailResponseId(r.id)}
                            data-testid={`console-response-row-${r.id}`}>
                        <td className="py-1.5 px-2">
                          <div className="font-medium text-primary-900 flex items-center gap-1.5"
                                data-testid={`console-response-name-${r.id}`}>
                            {r.recipient_name || '(no name on file)'}
                            {r.is_test ? (
                              <Badge
                                className="bg-sky-50 text-sky-800 border-sky-200 border text-[10px] px-1.5 py-0"
                                data-testid={`console-row-test-pill-${r.id}`}
                              >
                                test
                              </Badge>
                            ) : null}
                            {r.rating_excluded_from_aggregates ? (
                              <Badge
                                className="bg-rose-50 text-rose-800 border-rose-200 border text-[10px] px-1.5 py-0"
                                data-testid={`console-row-excluded-pill-${r.id}`}
                                title={
                                  'Excluded from rating aggregates. '
                                  + `Reason: ${r.rating_excluded_reason || 'n/a'}. `
                                  + `By: ${(r.rating_excluded_by || {}).admin_email || 'n/a'}. `
                                  + `At: ${r.rating_excluded_at || 'n/a'}`
                                }
                              >
                                Excluded from ratings
                              </Badge>
                            ) : null}
                          </div>
                          <div className="text-[10px] text-neutral-500"
                                data-testid={`console-response-email-${r.id}`}>
                            {r.email || '-'}
                          </div>
                        </td>
                        <td className="py-1.5 px-2">
                          {variantResolved}
                          {r.is_migrated_from_pre_bundle89 ? (
                            <Badge className="ml-2 bg-red-50 text-red-800 border-red-200 border text-[10px]"
                                    data-testid={`console-row-migrated-${r.id}`}>
                              migrated
                            </Badge>
                          ) : null}
                          {r.audit_only ? (
                            <Badge className="ml-1 bg-neutral-100 text-neutral-700 border-neutral-200 border text-[10px]">
                              audit
                            </Badge>
                          ) : null}
                        </td>
                        <td className="py-1.5 px-2">{r.answers?.overall_rating || r.answers?.future_interest || '-'}</td>
                        <td className="py-1.5 px-2">{r.rating_counts_toward_event ? 'yes' : '-'}</td>
                        <td className="py-1.5 px-2">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '-'}</td>
                        <td className={`py-1.5 px-2 ${isDrifted ? 'text-red-800 font-semibold' : ''}`}>
                          {r.answers?.self_reported_discovery_source
                            || r.answers?.discovery_source || '-'}
                          {isDrifted ? (
                            <span className="ml-1 text-[10px] text-red-700"
                                    data-testid={`console-row-drift-${r.id}`}>
                              [drift]
                            </span>
                          ) : null}
                        </td>
                        <td className="py-1.5 px-2"
                              onClick={(e) => e.stopPropagation()}>
                          {!r.submitted_at ? (
                            <span className="text-[10px] text-neutral-400"
                                    data-testid={`console-row-intros-pending-${r.id}`}>
                              pending
                            </span>
                          ) : !r.has_opt_in ? (
                            <Badge
                              className="bg-neutral-100 text-neutral-700 border-neutral-200 border text-[10px]"
                              data-testid={`console-row-intros-no-optin-${r.id}`}
                            >
                              No opt-in
                            </Badge>
                          ) : (r.intro_rows_count || 0) > 0 ? (
                            <Badge
                              className="bg-emerald-50 text-emerald-800 border-emerald-200 border text-[10px]"
                              data-testid={`console-row-intros-count-${r.id}`}
                            >
                              Intros: {r.intro_rows_count}
                            </Badge>
                          ) : (
                            <Badge
                              className="bg-amber-50 text-amber-800 border-amber-200 border text-[10px]"
                              data-testid={`console-row-intros-none-${r.id}`}
                            >
                              No intros
                            </Badge>
                          )}
                        </td>
                        {isSuperAdmin && (
                          <td className="py-1.5 px-2"
                                onClick={(e) => e.stopPropagation()}>
                            {!r.submitted_at ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-[10px] h-6 px-2"
                                onClick={() => setForceCompleteRow(r)}
                                data-testid={`console-force-complete-btn-${r.id}`}
                              >
                                Force complete
                              </Button>
                            ) : r.has_opt_in ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-[10px] h-6 px-2"
                                onClick={() => openRescanForRow(r)}
                                data-testid={`console-rescan-btn-${r.id}`}
                              >
                                {(r.intro_rows_count || 0) === 0
                                  ? 'Replay missed hooks'
                                  : 'Rescan intent'}
                              </Button>
                            ) : (
                              <span className="text-[10px] text-neutral-400">
                                Completed
                              </span>
                            )}
                            {/* Bundle 181 - super-admin only.
                                Only offered on submitted rows
                                (there's nothing to exclude on a
                                started but unsubmitted row). */}
                            {r.submitted_at ? (
                              r.rating_excluded_from_aggregates ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-[10px] h-6 px-2 ml-1 border-primary-300 text-primary-800 hover:bg-primary-50"
                                  onClick={() => {
                                    setExcludeRow(r);
                                    setExcludeMode('include');
                                  }}
                                  data-testid={`console-include-ratings-btn-${r.id}`}
                                >
                                  Include ratings
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-[10px] h-6 px-2 ml-1 border-rose-300 text-rose-800 hover:bg-rose-50"
                                  onClick={() => {
                                    setExcludeRow(r);
                                    setExcludeMode('exclude');
                                  }}
                                  data-testid={`console-exclude-ratings-btn-${r.id}`}
                                >
                                  Exclude ratings
                                </Button>
                              )
                            ) : null}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="testimonials" className="pt-4">
          {/* Bundle 159 - top strip: mandate confirmation panel +
              link to the dedicated AI approval queue. */}
          <div
            className="mb-3 flex items-start justify-between gap-3 flex-wrap rounded-md border border-primary-200 bg-primary-50/50 p-3"
            data-testid="testimonials-mandate-panel"
          >
            <div className="text-xs text-primary-900">
              <div className="font-semibold mb-0.5">
                Testimonial mandate is active
              </div>
              <div className="text-neutral-700">
                Since Bundle 156a, guests who consent must also
                submit the testimonial text on the survey. Legacy
                rows submitted before the mandate landed can carry
                consent with no text.{' '}
                <span
                  className="font-semibold text-primary-900"
                  data-testid="consented-empty-count-inline"
                >
                  {consentedEmptyRows.length}
                </span>
                {' '}legacy row{consentedEmptyRows.length === 1 ? '' : 's'}
                {' '}without text on this event. Use Draft with AI or
                Edit to complete them.
              </div>
            </div>
            <Link
              to="/admin/testimonials/approval-queue"
              className="text-xs font-semibold text-primary-800 hover:text-primary-900 underline whitespace-nowrap"
              data-testid="testimonials-full-approval-queue-link"
            >
              Full AI approval queue -&gt;
            </Link>
          </div>

          {/* Bundle 159 - filter chips. */}
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setTestimonialFilter('all')}
              className={
                'rounded-full border px-3 py-1 text-xs '
                + (testimonialFilter === 'all'
                  ? 'bg-primary-900 text-white border-primary-900'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:border-primary-400')
              }
              data-testid="testimonials-filter-all"
            >
              All ({testimonials.length})
            </button>
            <button
              type="button"
              onClick={() => setTestimonialFilter('consented_empty')}
              className={
                'rounded-full border px-3 py-1 text-xs '
                + (testimonialFilter === 'consented_empty'
                  ? 'bg-primary-900 text-white border-primary-900'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:border-primary-400')
              }
              data-testid="testimonials-filter-consented-empty"
            >
              Consented, no text ({consentedEmptyRows.length})
            </button>
          </div>

          <div className="space-y-3">
            {visibleTestimonials.map((t) => {
              const isConsentedEmpty = t.permission_granted
                && !((t.current_display_text || t.original_text || '').trim());
              const isEditing = testimonialEditRowId === t.id;
              const hasAiDraft = !!testimonialAiDraftById[t.id];
              return (
              <Card key={t.id}><CardContent className="p-3" data-testid={`testimonial-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-neutral-500 mb-1 flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{t.status}</Badge>
                      <span data-testid={`testimonial-attribution-${t.id}`}>
                        Attribution: <strong>{t.attribution_resolved || t.attribution || 'initials'}</strong>
                      </span>
                      <span>*</span>
                      <span data-testid={`testimonial-display-${t.id}`}>
                        Display: <strong>{t.display_name_resolved || t.display_name}</strong>
                      </span>
                      {isConsentedEmpty ? (
                        <>
                          <span>*</span>
                          <span
                            className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-800"
                            data-testid={`consented-empty-chip-${t.id}`}
                            title="Consent granted but no testimonial text. Use Draft with AI or Edit."
                          >
                            Consented, no text
                          </span>
                        </>
                      ) : null}
                      {t.text_edited_since_publish ? (
                        <>
                          <span>*</span>
                          <span
                            className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] text-blue-800"
                            data-testid={`edited-since-publish-chip-${t.id}`}
                          >
                            Edited since publish
                          </span>
                        </>
                      ) : null}
                      {t.needs_last_name ? (
                        <>
                          <span>*</span>
                          <span
                            className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-800"
                            data-testid={`needs-last-name-${t.id}`}
                            title="Backfill could not resolve a last name. Add one on the registration record."
                          >
                            Needs last name
                            {t.registration_id ? (
                              <>
                                {' '}
                                <Link
                                  to={`/admin/registrations/${t.registration_id}`}
                                  className="ml-1 underline"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`needs-last-name-link-${t.id}`}
                                >Edit</Link>
                              </>
                            ) : null}
                          </span>
                        </>
                      ) : null}
                      {typeof t.respondent_age === 'number' ? (
                        <>
                          <span>*</span>
                          <span data-testid={`testimonial-age-${t.id}`}>
                            Age <strong>{t.respondent_age}</strong>
                          </span>
                        </>
                      ) : null}
                      {t.respondent_gender ? (
                        <>
                          <span>*</span>
                          <span data-testid={`testimonial-gender-${t.id}`}>
                            Gender <strong>{t.respondent_gender}</strong>
                          </span>
                        </>
                      ) : null}
                    </div>
                    {isEditing ? (
                      <div className="space-y-2" data-testid={`testimonial-edit-${t.id}`}>
                        <Textarea
                          value={testimonialEditText}
                          onChange={(e) => setTestimonialEditText(e.target.value)}
                          rows={4}
                          className="text-sm"
                          placeholder="Testimonial text"
                          data-testid={`testimonial-edit-text-${t.id}`}
                        />
                        <Input
                          value={testimonialEditNote}
                          onChange={(e) => setTestimonialEditNote(e.target.value)}
                          placeholder="Audit note (optional, e.g. spelling fix)"
                          className="text-xs"
                          data-testid={`testimonial-edit-note-${t.id}`}
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            disabled={testimonialEditBusy}
                            onClick={() => saveEditTestimonial(t.id)}
                            className="bg-primary-900 text-white hover:bg-primary-800"
                            data-testid={`testimonial-edit-save-${t.id}`}
                          >Save edit</Button>
                          {hasAiDraft && !t.current_display_text ? (
                            <Button
                              size="sm"
                              disabled={testimonialEditBusy}
                              onClick={() => approveAiDraftForRow(t.id)}
                              className="bg-emerald-700 text-white hover:bg-emerald-800"
                              data-testid={`testimonial-edit-approve-${t.id}`}
                            >Approve &amp; publish</Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={testimonialEditBusy}
                            onClick={cancelEditTestimonial}
                            data-testid={`testimonial-edit-cancel-${t.id}`}
                          >Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-primary-900 leading-relaxed">
                        &ldquo;{t.current_display_text || t.original_text || <span className="italic text-neutral-400">no text yet</span>}&rdquo;
                      </div>
                    )}
                  </div>
                  {!isEditing ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      {isConsentedEmpty ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={testimonialDraftBusyId === t.id}
                          onClick={() => draftWithAiForRow(t)}
                          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                          data-testid={`testimonial-draft-ai-${t.id}`}
                        >
                          <Sparkles className="h-3.5 w-3.5 mr-1" />
                          {testimonialDraftBusyId === t.id ? 'Drafting...' : 'Draft with AI'}
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditTestimonial(t)}
                        data-testid={`testimonial-edit-btn-${t.id}`}
                      >Edit</Button>
                      {t.status !== 'approved' ? (
                        <Button size="sm" variant="outline" onClick={() => moderateTestimonial(t.id, 'approved')} data-testid={`testimonial-approve-${t.id}`}>Approve</Button>
                      ) : null}
                      {t.status !== 'published' && t.permission_granted ? (
                        <Button size="sm" onClick={() => moderateTestimonial(t.id, 'published')} className="bg-primary-900 text-white hover:bg-primary-800">Publish</Button>
                      ) : null}
                      {t.status !== 'permission_revoked' ? (
                        <Button size="sm" variant="outline" onClick={() => moderateTestimonial(t.id, 'permission_revoked')} className="text-red-700 border-red-200" data-testid={`testimonial-revoke-${t.id}`}>Revoke</Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </CardContent></Card>
              );
            })}
            {!visibleTestimonials.length ? (
              <div className="text-sm text-neutral-500" data-testid="testimonials-empty">
                {testimonialFilter === 'consented_empty'
                  ? 'No consented rows without text on this event.'
                  : 'No testimonials yet.'}
              </div>
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="corrections" className="pt-4">
          <div className="space-y-2">
            {corrections.map((c) => (
              <Card key={c.id}><CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`correction-${c.id}`}>
                <div className="flex-1">
                  <div className="text-xs text-neutral-500">
                    <Badge variant="outline" className="mr-2">{c.status}</Badge>
                    {c.email} * {new Date(c.created_at).toLocaleString()}
                  </div>
                  {c.note ? <div className="text-sm text-primary-800 mt-1">{c.note}</div> : null}
                </div>
                {c.status === 'pending' ? (
                  <div className="flex items-center gap-1">
                    <Button size="sm" onClick={() => verifyCorrection(c.id, true)} className="bg-emerald-700 text-white hover:bg-emerald-800" data-testid={`correction-verify-${c.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Verify
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => verifyCorrection(c.id, false)} className="text-red-700 border-red-200" data-testid={`correction-reject-${c.id}`}>
                      <XCircle className="h-3 w-3 mr-1" /> Reject
                    </Button>
                  </div>
                ) : null}
              </CardContent></Card>
            ))}
            {!corrections.length ? <div className="text-sm text-neutral-500">No pending corrections.</div> : null}
          </div>
        </TabsContent>

        <TabsContent value="connections" className="pt-4">
          <ConnectionsTabPanel eventId={eventId} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!sendResult} onOpenChange={(v) => { if (!v) setSendResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="send-live-result-dialog">
          <DialogHeader>
            <DialogTitle>Send live complete</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border"
                      data-testid="send-live-result-sent">Sent: {sendResult?.sent ?? 0}</Badge>
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 border"
                      data-testid="send-live-result-skipped">Skipped: {sendResult?.skipped ?? 0}</Badge>
            </div>
            {sendResult?.skipped_breakdown
              && Object.keys(sendResult.skipped_breakdown).length > 0 ? (
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">
                  Skipped by reason
                </div>
                <div className="flex flex-wrap gap-2"
                      data-testid="send-live-result-breakdown">
                  {Object.entries(sendResult.skipped_breakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([reason, count]) => (
                      <Badge key={reason} variant="outline"
                              className="text-[10px] bg-white text-red-800 border-red-200"
                              data-testid={`send-live-result-reason-${reason}`}>
                        {reason.replace(/_/g, ' ')}: {count}
                      </Badge>
                    ))}
                </div>
              </div>
            ) : null}
            {sendResult?.override_reasons_used?.length ? (
              <div className="text-xs text-primary-700">
                Overrides applied: {sendResult.override_reasons_used.join(', ')}
              </div>
            ) : null}
            <div className="text-xs text-neutral-500">
              See the Sent tab for the full per-recipient dispatched list.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setSendResult(null)}
                      className="bg-primary-900 text-white hover:bg-primary-800"
                      data-testid="send-live-result-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ResponseDetailModal
        eventId={eventId}
        responseId={detailResponseId}
        open={!!detailResponseId}
        onOpenChange={(v) => { if (!v) setDetailResponseId(null); }}
      />

      {/* Bundle 181 - Rating exclusion dialog (super-admin only) */}
      <ExcludeRatingsDialog
        open={!!excludeRow}
        row={excludeRow}
        mode={excludeMode}
        onOpenChange={(v) => { if (!v) setExcludeRow(null); }}
        onSuccess={() => { load(); }}
      />

      {/* Bundle 134 - Send reminders confirmation modal with dry-run counts + rendered preview */}
      <Dialog open={!!reminderModal}
                onOpenChange={(v) => { if (!v) setReminderModal(null); }}>
        <DialogContent className="max-w-2xl"
                            data-testid="send-reminder-modal">
          <DialogHeader>
            <DialogTitle>Send reminders</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded border border-neutral-200 bg-neutral-50 p-3 grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Eligible pool</div>
                <div className="text-primary-900 font-semibold"
                      data-testid="reminder-modal-eligible-pool">
                  {reminderModal?.eligible_pool ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Would send</div>
                <div className="text-emerald-800 font-semibold"
                      data-testid="reminder-modal-would-send">
                  {reminderModal?.would_send ?? reminderModal?.sent ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Opted-out</div>
                <div className="text-neutral-700"
                      data-testid="reminder-modal-opted-out">
                  {reminderModal?.skipped_opted_out ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Invalid email</div>
                <div className="text-red-700"
                      data-testid="reminder-modal-invalid-email">
                  {reminderModal?.skipped_invalid_email ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Already submitted</div>
                <div className="text-emerald-800"
                      data-testid="reminder-modal-already-submitted">
                  {reminderModal?.skipped_already_submitted ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Unresolvable wildcards</div>
                <div className="text-amber-800"
                      data-testid="reminder-modal-unresolvable">
                  {reminderModal?.skipped_unresolvable_wildcards ?? 0}
                </div>
              </div>
            </div>
            {reminderModal?.preview ? (
              <div className="space-y-2"
                    data-testid="reminder-modal-preview">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Sample email preview (to {reminderModal.preview.to_masked})
                </div>
                <div className="border border-neutral-200 rounded p-2">
                  <div className="text-xs text-neutral-500 mb-1">Subject</div>
                  <div className="text-sm text-primary-900"
                        data-testid="reminder-modal-preview-subject">
                    {reminderModal.preview.subject}
                  </div>
                </div>
                <details className="border border-neutral-200 rounded p-2">
                  <summary className="text-xs cursor-pointer text-primary-800">
                    Show rendered HTML body
                  </summary>
                  <div
                    className="mt-2 max-h-64 overflow-auto border-t border-neutral-100 pt-2"
                    data-testid="reminder-modal-preview-html"
                    dangerouslySetInnerHTML={{ __html: reminderModal.preview.html }}
                  />
                </details>
              </div>
            ) : (
              <div className="text-neutral-500 text-xs"
                    data-testid="reminder-modal-no-preview">
                No eligible recipients to preview.
              </div>
            )}
            {(reminderModal?.skipped_details || []).length > 0 ? (
              <details className="border border-amber-200 rounded p-2 bg-amber-50">
                <summary className="text-xs cursor-pointer text-amber-900">
                  {(reminderModal?.skipped_details || []).length} skipped
                  (click for reasons)
                </summary>
                <ul className="mt-2 text-[11px] space-y-0.5">
                  {(reminderModal?.skipped_details || []).map((s, i) => {
                    // Bundle 143 P0-3: show full email + name for
                    // already-submitted (operator needs to know who
                    // responded); still mask invalid/other reasons.
                    const showFull = s.reason === 'already_submitted';
                    const emailToShow = showFull
                      ? (s.email || s.email_masked)
                      : s.email_masked;
                    const nameToShow = showFull && s.name
                      ? `${s.name} <${emailToShow}>`
                      : emailToShow;
                    const pillClass = s.reason === 'already_submitted'
                      ? 'bg-green-100 text-green-800 border-green-300'
                      : 'bg-amber-100 text-amber-900 border-amber-300';
                    return (
                      <li key={i}
                          className="flex items-center gap-2"
                          data-testid={`reminder-modal-skipped-${s.invitation_id || i}`}>
                        <span className={`text-[10px] uppercase tracking-widest border rounded px-1 py-0.5 ${pillClass}`}>
                          {s.reason.replace(/_/g, ' ')}
                        </span>
                        <span data-testid={`reminder-modal-skipped-name-${s.invitation_id || i}`}>
                          {nameToShow}
                        </span>
                        {s.unresolvable_wildcards ? (
                          <span className="text-neutral-500">
                            ({s.unresolvable_wildcards.join(', ')})
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </details>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderModal(null)}
                        data-testid="reminder-modal-cancel-btn">Cancel</Button>
            <Button onClick={applyReminder} disabled={reminderBusy
                          || (reminderModal?.would_send ?? 0) === 0}
                        className="bg-primary-900 text-white hover:bg-primary-800"
                        data-testid="reminder-modal-confirm-btn">
              {reminderBusy ? 'Sending...' : 'Send reminders'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bundle 128 - Force-complete stuck survey response */}
      <Dialog open={!!forceCompleteRow}
                onOpenChange={(v) => { if (!v) { setForceCompleteRow(null); setForceReason(''); } }}>
        <DialogContent data-testid="force-complete-modal">
          <DialogHeader>
            <DialogTitle>Force-complete survey response</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p>
              This will finalize the response for
              <span className="font-semibold text-primary-900 ml-1"
                    data-testid="force-complete-target-name">
                {forceCompleteRow?.recipient_name || '(no name on file)'}
              </span>
              and replay every on-submit hook (side-effects, rating
              recompute, direct-exchange follow-up, missed-connection
              introductions insert).
            </p>
            <p className="text-neutral-600 text-xs">
              If the response is already submitted, this is a no-op
              and only the audit-log entry is written.
            </p>
            <label className="block text-xs uppercase tracking-widest text-neutral-500 mt-2">
              Reason (audit, required)
            </label>
            <Textarea
              rows={3}
              value={forceReason}
              onChange={(e) => setForceReason(e.target.value)}
              placeholder="Why is this happening? e.g. Guest submitted paper form on-site; system dropped connection at step 4."
              data-testid="force-complete-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline"
                    onClick={() => { setForceCompleteRow(null); setForceReason(''); }}
                    data-testid="force-complete-cancel-btn">Cancel</Button>
            <Button
              disabled={forceBusy || forceReason.trim().length < 3}
              onClick={runForceComplete}
              className="bg-primary-800 text-white hover:bg-primary-900"
              data-testid="force-complete-confirm-btn"
            >
              {forceBusy ? 'Working.' : 'Force complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bundle 130 amendment - Rescan/Replay via rescan-v2 */}
      <Dialog open={!!rescanRow}
                onOpenChange={(v) => { if (!v) { setRescanRow(null); setRescanDryRun(null); } }}>
        <DialogContent data-testid="rescan-modal">
          <DialogHeader>
            <DialogTitle>
              {rescanRow && (rescanRow.intro_rows_count || 0) === 0
                ? 'Replay missed intro hooks'
                : 'Rescan connection intent'}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p>
              For reporter
              <span className="font-semibold text-primary-900 ml-1"
                    data-testid="rescan-target-name">
                {rescanRow?.recipient_name || '(no name on file)'}
              </span>.
              This will insert any missing `introductions` rows for
              confirmed missed_connections on this response.
              Idempotent per (response_id, target, when_where).
            </p>
            <div className="rounded border border-neutral-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                Dry-run preview
              </div>
              {rescanDryRun ? (
                <div className="text-xs text-primary-900 space-y-1"
                        data-testid="rescan-dry-run">
                  <div>Response: <span className="font-mono">{rescanDryRun.response_id}</span></div>
                  <div>Source collection: {rescanDryRun.source_collection}</div>
                  <div>Would insert up to
                    <span className="font-semibold ml-1"
                          data-testid="rescan-would-insert">
                      {rescanDryRun.would_insert_introductions_max}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-neutral-500">Loading dry-run.</div>
              )}
            </div>
            <div className="rounded border border-primary-100 bg-primary-50/40 p-3">
              <div className="text-[10px] uppercase tracking-widest text-primary-800 mb-1">
                Planned targets
              </div>
              <ul className="list-disc pl-5 text-xs text-primary-900 space-y-0.5"
                    data-testid="rescan-planned-targets">
                {((rescanRow?.answers?.missed_connections) || []).filter((m) => m?.confirmation).length === 0 ? (
                  <li className="list-none text-neutral-500">
                    No confirmed missed_connections rows on this response.
                  </li>
                ) : ((rescanRow?.answers?.missed_connections) || [])
                    .filter((m) => m?.confirmation)
                    .map((m, i) => (
                      <li key={i}
                          data-testid={`rescan-target-${i}`}>
                        {m.target_first_name || m.first_name || '(unknown)'}
                        {m.when_where ? ` * ${m.when_where}` : ''}
                      </li>
                    ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline"
                    onClick={() => { setRescanRow(null); setRescanDryRun(null); }}
                    data-testid="rescan-cancel-btn">Cancel</Button>
            <Button
              disabled={rescanBusy || !rescanDryRun}
              onClick={applyRescanForRow}
              className="bg-primary-800 text-white hover:bg-primary-900"
              data-testid="rescan-apply-btn"
            >
              {rescanBusy ? 'Working.' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSurveyConsolePage;


// Bundle 78 Task 5 - inline survey preview. Fetches a real test
// invitation via /survey/send-test (which is idempotent per admin +
// variant), then iframes the public /survey/{token} route so the
// admin can walk through the exact copy a real recipient would see.
// Submissions from the iframe land in `test_survey_responses` so no
// preview click contaminates real analytics or the Bundle 77 rating
// cache.
const SurveyPreviewFrame = ({ eventId, variant }) => {
  const [token, setToken] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    setToken(null); setError(null);
    api.post(`/admin/events/${eventId}/survey/send-test`)
      .then((r) => {
        if (!mounted) return;
        const match = (r.data.results || []).find((x) => x.variant === variant);
        if (!match) { setError('No preview token returned.'); return; }
        setToken(match.token);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.response?.data?.detail || 'Preview failed.');
      });
    return () => { mounted = false; };
  }, [eventId, variant]);

  if (error) return <div className="text-red-600 text-sm" data-testid="survey-preview-error">{error}</div>;
  if (!token) return <div className="text-neutral-500 text-sm" data-testid="survey-preview-loading">Loading preview...</div>;
  return (
    <div className="border border-neutral-200 rounded-md overflow-hidden bg-white" data-testid="survey-preview-frame">
      <iframe
        title={`Survey preview ${variant}`}
        src={`/survey/${token}?preview_only=1`}
        className="w-full"
        style={{ minHeight: 640, border: 0 }}
      />
    </div>
  );
};
